import classnames from 'classnames';
import { get, includes, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  injectIntl
} from 'react-intl';
import { paymentStatus } from '../../app-bookings-current-details/data/payment-data';

const PaymentRow = ({ payment, intl }) => {
  const {
    date_created = '',
    date_modified = '',
    obj_state = '',
    method = '',
    details = {},
    method_desc = '',
    currency,
    amount = 0,
    amount_refunded,
    amount_credited,
    dts_payment_scheduled
  } = payment;

  const { brand = '', last4 = '', exp_month = '', exp_year = '' } = details;
  const status = get(paymentStatus, [obj_state], paymentStatus.ERROR);
  const stripePayment = 'stripe-api' === method;
  const psCreditPayment = 'ps-credit' === method;
  const totalStrikeThrough = includes(
    ['RELEASED', 'FAILED', 'RELEASING', 'CANCELLED', 'HELD'],
    obj_state
  );
  const ccRefund = 0 < amount_refunded;
  const creditRefund = 0 < amount_credited;
  const withdrawnCreditRefund = psCreditPayment && 0 < amount_refunded;

  return (
    <div className='payment-row'>
      <div className='flex space-between middle border-gray-200 payment-row-inner' data-testing-id="payment">
        <div className='flex-basis-30 flex-basis-20-sm' data-testing-id="payment-date">
          <FormattedDate
            value={dts_payment_scheduled || date_created}
            year='numeric'
            month='short'
            day='numeric'
          />
        </div>

        <div className='flex-basis-15 hidden-xs padding-lg-right' data-testing-id="payment-status">
          <span className={`status-${status.class} inline-block`}>
            <FormattedMessage {...status.text} />
          </span>
        </div>

        <div className='flex-basis-40' data-testing-id="payment-details">
          {stripePayment && !isEmpty(details) ? (
            `${brand} - ${last4}, ${intl.formatMessage({
              id: 'inbox.payment_row.expires',
              defaultMessage: 'Exp.'
            })} ${exp_month}/${exp_year}`
          ) : psCreditPayment ? (
            <FormattedMessage
              id='inbox.payment_row.peerspace_credit_details'
              defaultMessage='Peerspace Credit'
            />
          ) : (
            method_desc
          )}
        </div>

        <div
          className={classnames('flex-basis-15 text-right', {
            'strike-through': totalStrikeThrough
          })}
          data-testing-id="payment-amount"
        >
          <FormattedNumber
            value={amount}
            currency={currency}
            style={'currency'}
          />
        </div>
      </div>

      {ccRefund && !withdrawnCreditRefund && (
        <div className='flex space-between middle border-gray-200 payment-row-inner margin-xl-top' data-testing-id="cc-refund">
          <div className='flex-basis-30 flex-basis-20-sm' data-testing-id="cc-refund-date">
            <FormattedDate
              value={date_modified}
              year='numeric'
              month='short'
              day='numeric'
            />
          </div>

          <div className='flex-basis-15 hidden-xs padding-lg-right' data-testing-id="cc-refund-status">
            <span className='status-blue inline-block'>
              <FormattedMessage
                id='inbox.payment_row.refund'
                defaultMessage='Refund'
              />
            </span>
          </div>

          <div className='flex-basis-40' data-testing-id="cc-refund-details">
            {stripePayment && !isEmpty(details)
              ? `${brand} - ${last4}, ${intl.formatMessage({
                  id: 'inbox.payment_row.expires_2',
                  defaultMessage: 'Exp.'
                })} ${exp_month}/${exp_year}`
              : method_desc}
          </div>

          <div className='flex-basis-15 text-right' data-testing-id="cc-refund-amount">
            -
            <FormattedNumber
              value={amount_refunded}
              currency={currency}
              style={'currency'}
            />
          </div>
        </div>
      )}

      {(creditRefund || withdrawnCreditRefund) && (
        <div className='flex space-between middle border-gray-200 payment-row-inner margin-xl-top' data-testing-id="credit-refund">
          <div className='flex-basis-30 flex-basis-20-sm' data-testing-id="credit-refund-date">
            <FormattedDate
              value={date_modified}
              year='numeric'
              month='short'
              day='numeric'
            />
          </div>

          <div className='flex-basis-15 hidden-xs padding-lg-right' data-testing-id="credit-refund-status">
            <span className='status-blue inline-block'>
              <FormattedMessage
                id='inbox.payment_row.credit_refund'
                defaultMessage='Refund'
              />
            </span>
          </div>

          <div className='flex-basis-40' data-testing-id="credit-refund-details">
            <FormattedMessage
              id='inbox.payment_row.peerspace_credit'
              defaultMessage='Peerspace Credit'
            />
          </div>

          <div className='flex-basis-15 text-right' data-testing-id="credit-refund-amount">
            -
            <FormattedNumber
              value={withdrawnCreditRefund ? amount_refunded : amount_credited}
              currency={currency}
              style={'currency'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

PaymentRow.propTypes = {
  payment: PropTypes.object
};

export default injectIntl(PaymentRow);
